import { ProductResponseDTO } from "@shared/DTO";
import { Mount } from "@shared/enums";
import { isMount, MountDefinition } from "@shared/values";
import { getLensMountAffiliates } from "@/scripts/utils/lens-mount-affiliates.utils";

export interface PurchaseVariantOption {
  mount: MountDefinition;
  url?: string;
}

export type PurchaseCtaModel =
  | { type: "direct"; url: string }
  | { type: "selector"; options: readonly PurchaseVariantOption[] }
  | { type: "unavailable" };

export interface PurchaseScope {
  lensMounts: readonly Mount[];
}

export type ProductPurchaseScopes = Readonly<Record<string, PurchaseScope>>;

/** Validate the new public input without normalizing domain values. */
export const parseProductPurchaseScopes = (value: unknown): ProductPurchaseScopes => {
  if (value === undefined) return Object.freeze({});
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError("purchaseScopes must be an object indexed by product _id");
  }
  const entries = Object.entries(value).map(([id, scope]) => {
    if (!id.length || !scope || typeof scope !== "object" || Array.isArray(scope)
      || Object.keys(scope).length !== 1 || !("lensMounts" in scope)) {
      throw new TypeError("Each purchaseScopes entry must contain only lensMounts");
    }
    const mounts = scope.lensMounts;
    if (!Array.isArray(mounts) || !mounts.every(isMount) || new Set(mounts).size !== mounts.length) {
      throw new TypeError("purchaseScopes.lensMounts must contain unique valid mounts");
    }
    // An explicit empty scope allows no versions; it never means unrestricted.
    return [id, Object.freeze({ lensMounts: Object.freeze([...mounts]) })] as const;
  });
  return Object.freeze(Object.fromEntries(entries));
};

export const getDirectPurchaseCta = (url?: string): PurchaseCtaModel => (
  url?.length ? { type: "direct", url } : { type: "unavailable" }
);

export const getMountPurchaseCta = (options: readonly PurchaseVariantOption[]): PurchaseCtaModel => {
  if (!options.length) return { type: "unavailable" };
  if (options.length === 1) return getDirectPurchaseCta(options[0].url);
  return { type: "selector", options };
};

export const getProductPurchaseCta = (
  product: ProductResponseDTO,
  scope?: PurchaseScope,
): PurchaseCtaModel => {
  if (!product.lensData?.mounts?.length) {
    return scope ? { type: "unavailable" } : getDirectPurchaseCta(product.affiliate?.at(0)?.url);
  }
  const allowed = scope && new Set(scope.lensMounts);
  const options = getLensMountAffiliates(product)
    .filter(({ mount }) => !allowed || allowed.has(mount.public_id))
    .map(({ mount, affiliate }) => ({ mount, url: affiliate?.url }));
  return getMountPurchaseCta(options);
};
